import { useTranslation } from 'react-i18next';
import type { ISubcontractor } from '@interfaces/ISubcontractorPortal';
import { formatFullDate } from '../subcontractors.format';
import styles from './ContractorHero.module.css';

export function ContractorHero({ contractor, tz }: { contractor: ISubcontractor; tz: string }) {
  const { t } = useTranslation();

  return (
    <section className={styles.hero}>
      <div className={styles.top}>
        <div className={styles.identity}>
          <span className={styles.kicker}>{t('subcontractors.hero.kicker')}</span>
          <div className={styles.nameRow}>
            <h2 className={styles.name}>{contractor.name}</h2>
            <span className={styles.statusPill} data-status={contractor.status}>
              <span className={styles.statusDot} aria-hidden="true" />
              {t(`subcontractors.status.${contractor.status}`)}
            </span>
          </div>
          <p className={styles.scope}>{contractor.scope}</p>
        </div>

        <dl className={styles.metrics}>
          <div className={styles.metric}>
            <dt className={styles.metricLabel}>{t('subcontractors.hero.location')}</dt>
            <dd className={styles.metricValue}>
              {t('subcontractors.hero.locationValue', {
                zone: contractor.location.zone,
                level: contractor.location.level,
              })}
            </dd>
          </div>
          <div className={styles.metric}>
            <dt className={styles.metricLabel}>{t('subcontractors.hero.workforce')}</dt>
            <dd className={styles.metricValue}>
              {t('subcontractors.hero.workforceValue', { count: contractor.workforceCount })}
            </dd>
          </div>
          <div className={styles.metric}>
            <dt className={styles.metricLabel}>{t('subcontractors.hero.lead')}</dt>
            <dd className={styles.metricValue}>{contractor.leadName}</dd>
          </div>
        </dl>
      </div>

      <div className={styles.progressBlock}>
        <div className={styles.progressHead}>
          <span className={styles.progressLabel}>
            {t('subcontractors.hero.progress')}
          </span>
          <span className={styles.progressValue}>{contractor.progressPercent}%</span>
        </div>
        <div
          className={styles.progressBar}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={contractor.progressPercent}
        >
          <span
            className={styles.progressFill}
            style={{ width: `${contractor.progressPercent}%` }}
          />
        </div>
        <div className={styles.milestone}>
          <span className={styles.milestoneTitle}>{contractor.milestone}</span>
          <span className={styles.milestoneDate}>
            {t('subcontractors.hero.due', { date: formatFullDate(contractor.milestoneDate, tz) })}
          </span>
        </div>
      </div>
    </section>
  );
}
